import { useState, useEffect } from 'react';
import { api } from '../lib/api';

export default function AgencyDashboard({ user, isOwnerOrPartner }) {
  const [stats, setStats] = useState({
    totalClients: 0,
    activeClients: 0,
    leadsCount: 0,
    tasksCount: 0,
    overdueTasks: 0,
    completedToday: 0,
    revenue: 0,
    pendingPayments: 0
  });
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [staffPerformances, setStaffPerformances] = useState([]);

  const loadDashboardData = () => {
    setLoading(true);
    Promise.all([
      api.get('/api/client-module/clients').catch(() => []),
      api.get('/api/agency-crm/tasks').catch(() => []),
      api.get('/api/agency-crm/leads').catch(() => []),
      api.get('/api/agency-crm/invoices').catch(() => []),
      api.get('/api/staff-module/staff').catch(() => []),
    ])
      .then(([clients, allTasks, leads, invoices, staff]) => {
        const totalC = clients.length;
        const activeC = clients.filter(c => c.status === 'Active').length;
        const leadsC = leads.length;
        const tasksC = allTasks.length;
        
        const todayStr = new Date().toISOString().split('T')[0];
        const overdueT = allTasks.filter(t => t.status !== 'Completed' && t.dueDate && t.dueDate < todayStr).length;
        const completedT = allTasks.filter(t => t.status === 'Completed' && t.completedDate === todayStr).length;

        // Finance calculations
        const revenue = invoices.filter(iv => iv.status === 'Paid').reduce((sum, iv) => sum + (iv.amount || 0), 0);
        const pendingPay = invoices.filter(iv => iv.status === 'Pending').reduce((sum, iv) => sum + (iv.amount || 0), 0);

        setStats({
          totalClients: totalC,
          activeClients: activeC,
          leadsCount: leadsC,
          tasksCount: tasksC,
          overdueTasks: overdueT,
          completedToday: completedT,
          revenue,
          pendingPayments: pendingPay
        });

        // Set recent tasks
        setTasks(allTasks.slice(0, 5));

        // Staff Performance Engine
        const performance = staff.map(stf => {
          const stfTasks = allTasks.filter(t => t.assignedStaff === stf.name);
          const done = stfTasks.filter(t => t.status === 'Completed').length;
          const total = stfTasks.length;
          const pct = total > 0 ? Math.round((done / total) * 100) : 0;
          return { name: stf.name, done, total, pct };
        });
        setStaffPerformances(performance);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>Loading Agency CRM Dashboard…</div>;
  }

  // Render Owner / Manager dashboard
  if (isOwnerOrPartner) {
    return (
      <div style={{ fontFamily: 'system-ui, sans-serif' }}>
        {/* 4 Stat Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 20, marginBottom: 30 }}>
          <div style={{ background: 'rgba(255, 255, 255, 0.65)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 16, padding: 22, boxShadow: '0 8px 32px rgba(0,0,0,0.03)' }}>
            <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Active Clients</div>
            <div style={{ fontSize: '2.2rem', fontWeight: 900, color: '#0f172a', marginTop: 6 }}>{stats.activeClients} <span style={{ fontSize: '1rem', fontWeight: 500, color: '#64748b' }}>/ {stats.totalClients} total</span></div>
            <div style={{ width: '100%', height: 6, background: '#e2e8f0', borderRadius: 10, marginTop: 12, overflow: 'hidden' }}>
              <div style={{ width: `${stats.totalClients > 0 ? (stats.activeClients / stats.totalClients) * 100 : 0}%`, height: '100%', background: '#224eae' }} />
            </div>
          </div>

          <div style={{ background: 'rgba(255, 255, 255, 0.65)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 16, padding: 22, boxShadow: '0 8px 32px rgba(0,0,0,0.03)' }}>
            <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Today's Tasks Status</div>
            <div style={{ fontSize: '2.2rem', fontWeight: 900, color: '#0f172a', marginTop: 6 }}>{stats.completedToday} <span style={{ fontSize: '1rem', fontWeight: 500, color: '#16a34a' }}>Completed</span></div>
            <div style={{ fontSize: '0.82rem', color: '#dc2626', fontWeight: 700, marginTop: 8 }}>⚠️ {stats.overdueTasks} Overdue Tasks pending</div>
          </div>

          <div style={{ background: 'rgba(255, 255, 255, 0.65)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 16, padding: 22, boxShadow: '0 8px 32px rgba(0,0,0,0.03)' }}>
            <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Monthly Paid Invoices</div>
            <div style={{ fontSize: '2.2rem', fontWeight: 900, color: '#16a34a', marginTop: 6 }}>₹{stats.revenue.toLocaleString()}</div>
            <div style={{ fontSize: '0.82rem', color: '#64748b', marginTop: 8 }}>Fully synchronized with sheets</div>
          </div>

          <div style={{ background: 'rgba(255, 255, 255, 0.65)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 16, padding: 22, boxShadow: '0 8px 32px rgba(0,0,0,0.03)' }}>
            <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Pending Receivables</div>
            <div style={{ fontSize: '2.2rem', fontWeight: 900, color: '#d97706', marginTop: 6 }}>₹{stats.pendingPayments.toLocaleString()}</div>
            <div style={{ fontSize: '0.82rem', color: '#dc2626', fontWeight: 700, marginTop: 8 }}>Follow-up alerts sent</div>
          </div>
        </div>

        {/* 2-Column Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 24 }}>
          {/* Recent Tasks List */}
          <div style={{ background: '#fff', borderRadius: 20, padding: 26, border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '1.1rem', fontWeight: 800 }}>Recent Deliverables List</h3>
            {tasks.length === 0 ? (
              <div style={{ color: '#94a3b8', textAlign: 'center', padding: '30px 0' }}>No tasks generated yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {tasks.map(t => (
                  <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#f8fafc', borderRadius: 12, border: '1px solid #f1f5f9' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{t.clientName}</div>
                      <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 2 }}>{t.serviceName}</div>
                    </div>
                    <span style={{
                      background: t.status === 'Completed' ? '#dcfce7' : t.status === 'Editing' ? '#dbeafe' : '#fef3c7',
                      color: t.status === 'Completed' ? '#16a34a' : t.status === 'Editing' ? '#2563eb' : '#d97706',
                      padding: '4px 10px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 800
                    }}>{t.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Staff Performance */}
          <div style={{ background: '#fff', borderRadius: 20, padding: 26, border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '1.1rem', fontWeight: 800 }}>Staff Performance Tracker</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {staffPerformances.map(stf => (
                <div key={stf.name}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', fontWeight: 700, marginBottom: 6 }}>
                    <span>{stf.name}</span>
                    <span style={{ color: '#224eae' }}>{stf.done} / {stf.total} Tasks ({stf.pct}%)</span>
                  </div>
                  <div style={{ width: '100%', height: 8, background: '#f1f5f9', borderRadius: 10, overflow: 'hidden' }}>
                    <div style={{ width: `${stf.pct}%`, height: '100%', background: '#224eae', borderRadius: 10 }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render Staff dashboard
  const myTasks = tasks.filter(t => t.assignedStaff === user.name);
  const myPending = myTasks.filter(t => t.status !== 'Completed');
  const myDone = myTasks.filter(t => t.status === 'Completed');

  return (
    <div style={{ fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ background: '#224eae', color: '#fff', borderRadius: 20, padding: 28, marginBottom: 30 }}>
        <h2 style={{ margin: 0, fontWeight: 900 }}>Hello, {user.name}!</h2>
        <p style={{ margin: '6px 0 0', opacity: 0.9, fontSize: '0.95rem' }}>You have {myPending.length} pending deliverables assigned to you today.</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
        <div style={{ background: '#fff', borderRadius: 20, padding: 26, border: '1px solid #e2e8f0' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1.1rem', fontWeight: 800, color: '#dc2626' }}>My Pending Work</h3>
          {myPending.length === 0 ? (
            <div style={{ color: '#94a3b8', textAlign: 'center', padding: '30px 0' }}>All tasks completed! Good job.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {myPending.map(t => (
                <div key={t.id} style={{ padding: 14, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12 }}>
                  <div style={{ fontWeight: 800, fontSize: '0.9rem' }}>{t.clientName}</div>
                  <div style={{ fontSize: '0.8rem', color: '#dc2626', marginTop: 4 }}>{t.serviceName}</div>
                  <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 8 }}>Due Date: {t.dueDate}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ background: '#fff', borderRadius: 20, padding: 26, border: '1px solid #e2e8f0' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1.1rem', fontWeight: 800, color: '#16a34a' }}>Completed Tasks ({myDone.length})</h3>
          {myDone.length === 0 ? (
            <div style={{ color: '#94a3b8', textAlign: 'center', padding: '30px 0' }}>No tasks completed yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {myDone.map(t => (
                <div key={t.id} style={{ padding: 14, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12 }}>
                  <div style={{ fontWeight: 800, fontSize: '0.9rem', color: '#16a34a' }}>{t.clientName}</div>
                  <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 4 }}>{t.serviceName}</div>
                  <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 8 }}>Done on: {t.completedDate}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
